<template>
    <DefaultHeader />
    <div :class="{ 'd-none': $store.state.hiddenBody }" class="container-fluid">
        <div class="row d-flex justify-content-center">
            <div class="col-12 col-xl-10 mb-3 mb-lg-5">
                <div class="card shadow-sm border-0 mb-4 revenue-card">
                    <div class="card-header py-3 bg-white border-0">
                        <h6 class="m-0 font-weight-bold text-primary">
                            Progression du CA
                        </h6>
                    </div>

                    <div class="card-body">
                        <div v-if="loading" class="text-center py-5">
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Chargement...</span>
                            </div>
                        </div>

                        <div v-else>
                            <div class="revenue-chart-wrapper">
                                <VChart
                                    v-if="chartReady"
                                    class="revenue-chart"
                                    :option="chartOptions"
                                    autoresize
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <DefaultFooter />
</template>

<script>
import DefaultHeader from "@/components/DefaultHeader.vue";
import DefaultFooter from "@/components/DefaultFooter.vue";
import BillingService from "@/services/billing.service.js";
import * as echarts from "echarts/core";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
} from "echarts/components";
import VChart from "vue-echarts";

use([
    CanvasRenderer,
    BarChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
]);

export default {
    components: {
        DefaultHeader,
        DefaultFooter,
        VChart,
    },
    data() {
        return {
            loading: false,
            chartReady: false,
            startMonth: "2025-09",
            endMonth: this.getPreviousMonth(),
            chartOptions: {
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "shadow",
                    },
                    backgroundColor: "rgba(255, 255, 255, 0.96)",
                    borderColor: "#e9ecef",
                    borderWidth: 1,
                    textStyle: {
                        color: "#212529",
                    },
                    extraCssText: "box-shadow: 0 12px 30px rgba(0,0,0,0.10); border-radius: 12px;",
                    formatter: (params) => {
                        const item = params?.[0];
                        if (!item) return "";

                        const value = Number(item.value || 0);

                        return `
                            <div style="font-weight:600; margin-bottom:6px;">${item.axisValue}</div>
                            <div>CA : ${new Intl.NumberFormat("fr-FR", {
                                style: "currency",
                                currency: "EUR",
                                minimumFractionDigits: 2,
                            }).format(value)}</div>
                        `;
                    },
                },
                grid: {
                    left: 12,
                    right: 12,
                    top: 20,
                    bottom: 60,
                    containLabel: true,
                },
                xAxis: {
                    type: "category",
                    data: [],
                    axisTick: {
                        show: false,
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#dee2e6",
                        },
                    },
                    axisLabel: {
                        color: "#6c757d",
                        fontSize: 11,
                        interval: 0,
                        rotate: 35,
                    },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    splitLine: {
                        lineStyle: {
                            color: "#f5f6f8",
                        },
                    },
                    axisLabel: {
                        color: "#6c757d",
                        formatter: (value) => {
                            return new Intl.NumberFormat("fr-FR", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 0,
                            }).format(value);
                        },
                    },
                },
                series: [
                    {
                        name: "CA",
                        type: "bar",
                        data: [],
                        barWidth: 22,
                        itemStyle: {
                            borderRadius: [4, 4, 0, 0],
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: "#3b82f6" },
                                { offset: 1, color: "#1d4ed8" },
                            ]),
                        },
                        emphasis: {
                            itemStyle: {
                                color: "#2563eb",
                            },
                        },
                    },
                ],
                animationDuration: 900,
                animationEasing: "cubicOut",
            },
        };
    },
    mounted() {
        this.fetchRevenueProgress();
    },
    methods: {
        getPreviousMonth() {
            const date = new Date();
            date.setDate(1);
            date.setMonth(date.getMonth() - 1);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            return `${year}-${month}`;
        },

        formatMonthLabel(month) {
            const [year, monthIndex] = month.split("-");
            return `${monthIndex}/${String(year).slice(-2)}`;
        },

        getMonthRange(start, end) {
            const months = [];

            let [year, month] = start.split("-").map(Number);
            const [endYear, endMonth] = end.split("-").map(Number);

            while (year < endYear || (year === endYear && month <= endMonth)) {
                months.push(
                    `${year}-${String(month).padStart(2, "0")}`
                );

                month++;
                if (month > 12) {
                    month = 1;
                    year++;
                }
            }

            return months;
        },

        buildChartData(apiData) {
            const months = this.getMonthRange(this.startMonth, this.endMonth);

            const totalsByMonth = {};
            for (const row of apiData) {
                const month = String(row.month).slice(0, 7);
                totalsByMonth[month] = Number(row.total || 0);
            }

            return months.map((month) => ({
                label: this.formatMonthLabel(month),
                value: totalsByMonth[month] || 0,
            }));
        },
        updateChartOptions(rows) {
            this.chartOptions = {
                ...this.chartOptions,
                xAxis: {
                    ...this.chartOptions.xAxis,
                    data: rows.map((row) => row.label),
                },
                series: [
                    {
                        ...this.chartOptions.series[0],
                        data: rows.map((row) => row.value),
                    },
                ],
            };
         },
        fetchRevenueProgress() {
            this.loading = true;
            this.chartReady = false;

            BillingService.getRevenueProgress(this.startMonth, this.endMonth)
                .then((response) => {
                    const rows = this.buildChartData(response.data || []);
                    this.updateChartOptions(rows);
                })
                .catch((err) => {
                    console.error("Erreur récupération progression CA :", err);
                    this.updateChartOptions([
                        { label: "Aucune donnée", value: 0 }
                    ]);
                })
                .finally(() => {
                    this.loading = false;
                    this.$nextTick(() => {
                        this.chartReady = true;
                    });
                });
        },
    },
};
</script>

<style scoped>
.revenue-card {
    border-radius: 18px;
}

.revenue-chart-wrapper {
    height: 600px;
    overflow: hidden;
}

.revenue-chart {
    height: 100%;
    width: 100%;
}
</style>